import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../store';
import type { RootState } from '../store';
import { useAblyClient } from '../contexts/AblyContext';
import { getMessages, prependMessage } from '../store/actions/messages';
import { unreadInboxCount } from '../utilities/inboxUnread';
import config from '../config';

/**
 * A plan's unread inbox count for the Team tab and the sidebar's Inbox. It loads
 * the plan's mail (getMessages keeps it for a minute) and listens on the inbox
 * channel InboxScreen uses, so a reply shows up without opening the inbox.
 * prependMessage drops duplicates, so both listening at once is harmless.
 */
export function usePlanInboxUnread(projectId: number | string | null | undefined, enabled = true): number {
  const dispatch = useAppDispatch();
  const { client } = useAblyClient();
  const accountId = useSelector((s: RootState) => (s as any).session?.account?.account_id as string | undefined);
  const cognitoId = useSelector((s: RootState) => (s as any).authentication?.user?.cognito_id as string | undefined);
  const key = projectId == null || projectId === '' ? null : String(projectId);
  const count = useSelector((s: RootState) => (key ? unreadInboxCount((s as any).messages?.list?.[key]) : 0));

  useEffect(() => {
    if (!enabled || key == null) return;
    dispatch(getMessages(key));
  }, [dispatch, enabled, key]);

  useEffect(() => {
    if (!enabled || !client || !accountId || !cognitoId || key == null) return undefined;
    const channelName = `message_inbox_${config.env}_${accountId}_${cognitoId}_${key}`;
    const eventName = `New Message ${key}`;
    const handler = (msg: any) => {
      if (msg?.data) dispatch(prependMessage(key, msg.data));
    };
    let channel: any;
    try {
      channel = client.channels.get(channelName);
      channel.subscribe(eventName, handler);
    } catch {}
    return () => {
      try { channel?.unsubscribe(eventName, handler); } catch {}
    };
  }, [accountId, client, cognitoId, dispatch, enabled, key]);

  return enabled ? count : 0;
}
